import { Box, ButtonBase, Divider, Stack, Typography } from '@mui/material';
import { ChangeEvent, ReactNode, useState } from 'react';

import { TextInput } from '@/components/form/baseInputs/TextInput';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { COLORS } from '@/styles/tokens/colors';

import {
  filterBySearchQuery,
  ORGANIZATION_DETAIL_PAGE_SIZE,
  paginateItems,
} from './AdminOrganizationsPage.utils';

interface OrganizationDetailsListProps<T> {
  items: T[];
  emptyMessage: string;
  getItemKey: (item: T) => string;
  getSearchableText: (item: T) => string;
  itemName: string;
  renderItem: (item: T) => ReactNode;
  searchLabel: string;
}

export function OrganizationDetailsList<T>({
  items,
  emptyMessage,
  getItemKey,
  getSearchableText,
  itemName,
  renderItem,
  searchLabel,
}: OrganizationDetailsListProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [requestedPage, setRequestedPage] = useState(1);
  // Not memoized: callers pass inline getSearchableText, so a memo would recompute
  // every render anyway. Filtering is cheap here (client-side over <= 100 items).
  const filteredItems = filterBySearchQuery(
    items,
    searchQuery,
    getSearchableText
  );
  const {
    items: visibleItems,
    page,
    startIndex,
    totalPages,
  } = paginateItems(filteredItems, requestedPage);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setRequestedPage(1);
  };

  if (items.length === 0) {
    return <Typography variant="body-xs">{emptyMessage}</Typography>;
  }

  return (
    <Stack spacing={1.5}>
      <TextInput
        label={searchLabel}
        hideLabel
        fullWidth
        placeholder={`${searchLabel}…`}
        startAdornment={<SearchSmIcon size={16} />}
        value={searchQuery}
        onChange={handleSearchChange}
      />

      {filteredItems.length === 0 ? (
        <Typography variant="body-xs">
          No {itemName} match “{searchQuery.trim()}”.
        </Typography>
      ) : (
        <Box
          sx={{
            border: `1px solid ${COLORS.GRAY[200]}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Stack divider={<Divider />}>
            {visibleItems.map((item) => (
              <Box key={getItemKey(item)}>{renderItem(item)}</Box>
            ))}
          </Stack>
        </Box>
      )}

      {filteredItems.length > ORGANIZATION_DETAIL_PAGE_SIZE && (
        <ListPagination
          itemName={itemName}
          page={page}
          totalPages={totalPages}
          startIndex={startIndex}
          visibleCount={visibleItems.length}
          totalCount={filteredItems.length}
          onPrevious={() => setRequestedPage(page - 1)}
          onNext={() => setRequestedPage(page + 1)}
        />
      )}
    </Stack>
  );
}

interface ListPaginationProps {
  itemName: string;
  page: number;
  totalPages: number;
  startIndex: number;
  visibleCount: number;
  totalCount: number;
  onPrevious: () => void;
  onNext: () => void;
}

function ListPagination({
  itemName,
  page,
  totalPages,
  startIndex,
  visibleCount,
  totalCount,
  onPrevious,
  onNext,
}: ListPaginationProps) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography variant="body-xs">
        Showing {startIndex + 1}–{startIndex + visibleCount} of {totalCount}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <PaginationButton
          label={`Previous ${itemName} page`}
          disabled={page === 1}
          onClick={onPrevious}
        >
          <ChevronLeftIcon size={16} />
        </PaginationButton>
        <Typography variant="body-xs">
          {page} / {totalPages}
        </Typography>
        <PaginationButton
          label={`Next ${itemName} page`}
          disabled={page === totalPages}
          onClick={onNext}
        >
          <ChevronRightIcon size={16} />
        </PaginationButton>
      </Stack>
    </Stack>
  );
}

interface PaginationButtonProps {
  children: ReactNode;
  disabled: boolean;
  label: string;
  onClick: () => void;
}

function PaginationButton({
  children,
  disabled,
  label,
  onClick,
}: PaginationButtonProps) {
  return (
    <ButtonBase
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 28,
        height: 28,
        border: `1px solid ${COLORS.GRAY[200]}`,
        borderRadius: '6px',
        color: COLORS.GRAY[700],
        '&:hover': { bgcolor: COLORS.GRAY[100] },
        '&.Mui-disabled': { opacity: 0.4 },
      }}
    >
      {children}
    </ButtonBase>
  );
}
